import { useCallback } from 'react';
import type { ChangeEvent } from 'react';

import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ensureDefined } from '@e-socna/utils';
import { PaletteModeEnum, SocnaColorEnum } from '@e-socna/data-layer';
import type { PaletteModeId } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';

import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { DEFAULT_THEME_COLOR } from '../../../../constants/socna-theme-colors.constant';
import { themeColorOf } from '../../../../utils/theme-color-of.util';

import { ColorSwatch } from './ColorSwatch';
import { useSaveUiSettings } from './hooks/useSaveUiSettings.hook';
import type { UiSettingsValues } from './hooks/useSaveUiSettings.hook';

interface Props {
  readonly isEditable: boolean;
  readonly uiSettings: UiSettingsValues;
}

const CONTEXTUAL_COLOR_LABEL =
  "Couleur de l'émetteur des devis dans les espaces dossier, mission et événement";

const SOCNA_COLORS: readonly string[] = Object.values(SocnaColorEnum);

const isSocnaColor = (value: string): value is SocnaColorEnum =>
  SOCNA_COLORS.includes(value);

export const DisplaySettings = ({
  isEditable,
  uiSettings,
}: Readonly<Props>) => {
  const { paletteModesCodeMap, paletteModesIdMap } = useReferencesContext();
  const { saveUiSettings } = useSaveUiSettings(uiSettings);

  const { paletteMode } = ensureDefined({
    paletteMode: paletteModesIdMap[uiSettings.paletteModeId as PaletteModeId],
  });
  const themeMainColor = themeColorOf(uiSettings.themeMainColor);

  const handlePaletteModeChange = useCallback(
    (_event: unknown, isDark: boolean) => {
      const { _id: paletteModeId } = isDark
        ? paletteModesCodeMap.Dark
        : paletteModesCodeMap.Light;

      saveUiSettings({ paletteModeId });
    },
    [paletteModesCodeMap, saveUiSettings],
  );

  const handleThemeMainColorChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      saveUiSettings({ themeMainColor: isSocnaColor(value) ? value : null });
    },
    [saveUiSettings],
  );

  const handleContextualColorChange = useCallback(
    (_event: unknown, isContextualColorEnabled: boolean) => {
      saveUiSettings({ isContextualColorEnabled });
    },
    [saveUiSettings],
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Typography gutterBottom variant="h6">
        Affichage
      </Typography>
      {isEditable ? (
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={paletteMode.code === PaletteModeEnum.Dark}
                id="user-settings-switch-palette-mode"
                name="paletteModeId"
                onChange={handlePaletteModeChange}
              />
            }
            label="Mode sombre"
          />
          <TextField
            id="user-settings-theme-main-color"
            label="Couleur principale"
            name="themeMainColor"
            onChange={handleThemeMainColorChange}
            select
            slotProps={{
              inputLabel: { shrink: true },
              select: { displayEmpty: true },
            }}
            sx={{ maxWidth: 360 }}
            value={uiSettings.themeMainColor ?? EMPTY_STRING}
          >
            <MenuItem value={EMPTY_STRING}>
              <ColorSwatch hex={DEFAULT_THEME_COLOR.hex} />
              {DEFAULT_THEME_COLOR.label}
            </MenuItem>
            {Object.values(SocnaColorEnum).map((socnaColor) => {
              const { hex, label } = themeColorOf(socnaColor);

              return (
                <MenuItem key={socnaColor} value={socnaColor}>
                  <ColorSwatch hex={hex} />
                  {label}
                </MenuItem>
              );
            })}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={uiSettings.isContextualColorEnabled}
                id="user-settings-switch-contextual-color"
                name="isContextualColorEnabled"
                onChange={handleContextualColorChange}
              />
            }
            label={CONTEXTUAL_COLOR_LABEL}
          />
        </Stack>
      ) : (
        <Stack spacing={1}>
          <Typography variant="body1">{paletteMode.label}</Typography>
          <Typography variant="body1">
            <ColorSwatch hex={themeMainColor.hex} />
            {themeMainColor.label}
          </Typography>
          <Typography variant="body1">
            {CONTEXTUAL_COLOR_LABEL} :{' '}
            {uiSettings.isContextualColorEnabled ? 'oui' : 'non'}
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};
